import { InfoLogs } from '@/constants/logs';
import { logInfo } from '@/utils/logger';

/**
 * Diagnostics for payments that fail by going silent (ACDEV-26410).
 *
 * Production shows mobile Google Pay 3DS completing ~43% against ~98% for card
 * 3DS in the same countries and issuers, and the failing sessions produce no
 * error at all — their last log lands 0.3-2.5s after the moment of interest and
 * then nothing. RUM cannot explain that on its own: the SDK ships spans on a 2s
 * batch timer with no unload-safe transport and no reachable flush (see the
 * note in `logger.ts`), so a page that stops executing takes its final logs
 * with it. More `logInfo` calls therefore cannot answer the question; the
 * instruments have to be built so their evidence has already been delivered by
 * the time the page dies.
 *
 * The three here are designed around that constraint:
 *   - a heartbeat coarser than the batch delay, so the last delivered beat
 *     bounds when the page stopped executing;
 *   - lifecycle markers, which the browser fires *before* it discards or
 *     freezes a page, separating "the OS took it" from "it just stopped";
 *   - a one-shot environment/geometry snapshot taken while the page is
 *     healthy, so the correlates of death (RAM, embedded browser, an
 *     unreachable iframe) are on record regardless of how it ends.
 */

/**
 * Coarser than the SDK's 2000ms batch delay, so a beat is normally already
 * shipped before the next one is due and at most one is lost to a kill.
 */
export const LIVENESS_HEARTBEAT_INTERVAL_MS = 3000;

/**
 * ~2 minutes of beats. Past that the challenge is lost for other reasons (the
 * ACS window closes around 10 minutes) and further beats only add volume.
 */
const MAX_HEARTBEATS = 40;

type DiagnosticContext = Record<string, unknown>;

interface NetworkInformation {
  effectiveType?: string;
  saveData?: boolean;
}

interface DiagnosticNavigator extends Navigator {
  deviceMemory?: number;
  connection?: NetworkInformation;
}

/**
 * Embedded-browser tokens. Whether the checkout is hosted in an app's webview
 * rather than standalone Chrome/Safari is load-bearing for the OS-kill
 * hypothesis — an embedded webview is cheaper for the OS to reclaim and has no
 * tab to return to — and it has never been measured, only assumed.
 */
const EMBEDDED_BROWSER_TOKENS: Array<[string, RegExp]> = [
  ['facebook', /FBAN|FBAV|FB_IAB/i],
  ['instagram', /Instagram/i],
  ['tiktok', /BytedanceWebview|musical_ly|TikTok/i],
  ['snapchat', /Snapchat/i],
  ['twitter', /Twitter/i],
  ['line', /\bLine\//i],
  ['wechat', /MicroMessenger/i],
  ['telegram', /Telegram/i],
  ['google-app', /\bGSA\//i],
  // Named apps first: an in-app browser on Android carries both its own token
  // and `; wv)`, and the app is the more useful attribution.
  ['android-webview', /;\s*wv\)/i],
];

/**
 * iOS has no `wv` marker, so an embedded WKWebView is identified by what it
 * lacks: every standalone iOS browser appends a `Safari/` token, and a
 * WKWebView does not. Chrome (`CriOS`) and Firefox (`FxiOS`) are standalone
 * browsers and must NOT be counted here — labelling them as webviews would
 * poison the cohort split these fields exist to make.
 */
const isIOSEmbeddedWebView = (userAgent: string): boolean =>
  /iPhone|iPad|iPod/i.test(userAgent) && /AppleWebKit/i.test(userAgent) && !/Safari\//i.test(userAgent);

const detectEmbeddedBrowser = (): string => {
  const { userAgent } = navigator;
  const match = EMBEDDED_BROWSER_TOKENS.find(([, pattern]) => pattern.test(userAgent));
  if (match) return match[0];

  return isIOSEmbeddedWebView(userAgent) ? 'ios-webview' : 'none';
};

/**
 * Correlates of a silent death, captured while the page is still healthy.
 * `deviceMemoryGb` is the direct test of the low-memory-kill hypothesis: if the
 * dying cohort skews to 2-4GB devices and the surviving one does not, the OS is
 * reclaiming the page rather than the shopper abandoning it.
 */
export const captureRuntimeEnvironment = (): DiagnosticContext => {
  const nav = navigator as DiagnosticNavigator;

  // Every optional field is stringified rather than left as `number | 'unknown'`.
  // A field that arrives as a number for some sessions and a string for others
  // is a union type in DataPrime, and grouping one fails outright — which would
  // break the very cohort queries these fields are here to serve.
  return {
    deviceMemoryGb: String(nav.deviceMemory ?? 'unknown'),
    cpuCores: String(nav.hardwareConcurrency ?? 'unknown'),
    connectionType: String(nav.connection?.effectiveType ?? 'unknown'),
    isDataSaver: String(nav.connection?.saveData ?? 'unknown'),
    embeddedBrowser: detectEmbeddedBrowser(),
    isFramed: window.self !== window.top,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    visibilityState: document.visibilityState,
  };
};

/**
 * Marks the browser-driven end of a page's life while `label` is in flight.
 *
 * `pagehide` and `freeze` both fire *before* the page stops running, so their
 * presence (or absence) is what separates the candidate mechanisms:
 *   - `pagehide` with `persisted: false` — the page is being discarded for good.
 *   - `pagehide` with `persisted: true` / `freeze` — the page is being cached or
 *     frozen and may resume. A frozen page runs no timers, which is what a
 *     heartbeat gap under these markers means.
 *   - nothing at all, heartbeats simply stopping — the process was killed with
 *     no warning, the one outcome no in-page log can announce for itself.
 *
 * These markers are queued like any other log and are themselves subject to the
 * 2s batch window, so a terminal one may not survive; they are corroboration
 * for the heartbeat, not a replacement. Nothing is flushed here on purpose —
 * the SDK's `shutdown()` deactivates the exporter without draining it, so
 * "flushing" on `pagehide` would delete the evidence (see `logger.ts`).
 */
export const watchPageLifecycle = (label: string, getContext?: () => DiagnosticContext): (() => void) => {
  const startedAt = Date.now();

  const logPhase = (phase: string, extra?: DiagnosticContext) => {
    logInfo(label, {
      phase,
      msSinceStart: Date.now() - startedAt,
      visibilityState: document.visibilityState,
      ...getContext?.(),
      ...extra,
    });
  };

  const handlePageHide = (event: PageTransitionEvent) => logPhase('pagehide', { persisted: event.persisted });
  const handleFreeze = () => logPhase('freeze');
  const handleResume = () => logPhase('resume');

  window.addEventListener('pagehide', handlePageHide);
  document.addEventListener('freeze', handleFreeze);
  document.addEventListener('resume', handleResume);

  return () => {
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('freeze', handleFreeze);
    document.removeEventListener('resume', handleResume);
  };
};

/**
 * Emits a beat every {@link LIVENESS_HEARTBEAT_INTERVAL_MS} so the last one
 * delivered dates the moment the page stopped executing — the measurement that
 * finally separates a killed page from a shopper who never finished. A session
 * whose beats stop at 4s died; one that beats for two minutes and never settles
 * was alive the whole time and the challenge itself is what failed.
 *
 * Read alongside `visibilityState`: a hidden page has its timers throttled to
 * roughly once a minute and is frozen outright after a few minutes, so widening
 * gaps while hidden are the browser conserving work, not evidence of death.
 */
export const startLivenessHeartbeat = (label: string, getContext?: () => DiagnosticContext): (() => void) => {
  const startedAt = Date.now();
  let beat = 0;

  const timer = setInterval(() => {
    beat += 1;
    // `msSinceStart` is deliberately the real elapsed time rather than
    // `beat * interval`: the difference between them is timer drift, which is
    // how a starved main thread or a throttled page shows up in the data.
    // `hasWindowFocus` catches the shopper moving to another app or window
    // while the page still reports itself visible, which `visibilityState`
    // alone cannot see.
    logInfo(label, {
      beat,
      msSinceStart: Date.now() - startedAt,
      visibilityState: document.visibilityState,
      hasWindowFocus: document.hasFocus(),
      ...getContext?.(),
    });
    if (beat >= MAX_HEARTBEATS) {
      clearInterval(timer);
    }
  }, LIVENESS_HEARTBEAT_INTERVAL_MS);

  return () => clearInterval(timer);
};

/**
 * Whether the painted challenge is actually reachable.
 *
 * `onActionHandled` fires for 100% of mounted challenges in production, which
 * has been read as "the iframe renders fine" — but it only reports that the SDK
 * handled the action, not that the result occupies usable space on screen. A
 * zero-height or off-viewport iframe looks identical in RUM today and would
 * present as a shopper who never responded.
 */
export const describeChallengeGeometry = (containerId: string): DiagnosticContext => {
  const container = document.getElementById(containerId);
  const iframe = container?.querySelector('iframe');
  const containerRect = container?.getBoundingClientRect();
  const iframeRect = iframe?.getBoundingClientRect();

  const isOnScreen = Boolean(
    iframeRect &&
    iframeRect.width > 0 &&
    iframeRect.height > 0 &&
    iframeRect.top < window.innerHeight &&
    iframeRect.bottom > 0
  );

  // No `viewport` here: it belongs to the environment snapshot this is logged
  // beside, and emitting it twice into one log is just a key collision.
  return {
    containerSize: containerRect ? `${Math.round(containerRect.width)}x${Math.round(containerRect.height)}` : 'missing',
    iframeSize: iframeRect ? `${Math.round(iframeRect.width)}x${Math.round(iframeRect.height)}` : 'missing',
    isChallengeOnScreen: isOnScreen,
  };
};

/**
 * Whether keyboard focus currently sits inside the challenge iframe.
 *
 * The challenge is cross-origin, so nothing inside it is readable — but moving
 * focus into an iframe makes it the parent document's `activeElement`, and that
 * much is observable. It is the only available proof that the shopper actually
 * engaged with the challenge, which is the difference between "read the screen
 * and gave up" and "typed a code and the result never came back". Those two
 * look identical in every other signal we have.
 *
 * Sampled rather than event-driven: focus entering a cross-origin iframe does
 * not reliably fire `focusin` on the parent, so callers poll this and keep the
 * answer sticky.
 */
export const isChallengeFocused = (containerId: string): boolean => {
  const iframe = document.getElementById(containerId)?.querySelector('iframe');
  return Boolean(iframe) && document.activeElement === iframe;
};

/**
 * Which issuer ACS is on the other end of the challenge.
 *
 * Every frontend signal we have is identical for a French challenge that
 * completes and a Spanish one that never does: same component, same token
 * type, same geometry, same heartbeat. What differs is who serves the
 * challenge. Adyen's threeDS2 action carries a base64 JSON token with the
 * `acsURL` the SDK will POST the CReq to (or `threeDSMethodUrl` for the
 * fingerprint step), so the host is available before the iframe exists.
 *
 * Host and protocol version only. The token also holds transaction ids that
 * would let a log line be tied back to Adyen's records, and nothing here
 * needs them.
 */
export const describeChallengeIssuer = (action: { token?: string }): DiagnosticContext => {
  if (!action.token) return { acsHost: 'no_token' };
  try {
    const payload = JSON.parse(atob(action.token.replace(/-/g, '+').replace(/_/g, '/'))) as {
      acsURL?: string;
      threeDSMethodUrl?: string;
      threeDSMethodURL?: string;
      messageVersion?: string;
    };
    const url = payload.acsURL ?? payload.threeDSMethodUrl ?? payload.threeDSMethodURL;
    if (!url) return { acsHost: 'missing' };
    return { acsHost: new URL(url).hostname, threeDSMessageVersion: payload.messageVersion ?? 'unknown' };
  } catch {
    return { acsHost: 'undecodable' };
  }
};

/**
 * Proof that a previous page in this tab was discarded by the browser rather
 * than closed by the shopper. Chrome sets `wasDiscarded` on the restored
 * document, so it is the one kill signal that survives the kill itself —
 * available only if the tab is ever reopened, which production RUM suggests is
 * rare, so it corroborates rather than measures.
 */
export const logTabRestoreIfDiscarded = (): void => {
  if (!(document as Document & { wasDiscarded?: boolean }).wasDiscarded) return;

  logInfo(InfoLogs.checkoutTabRestoredAfterDiscard, captureRuntimeEnvironment());
};
